// PaletteProvider — context (isOpen/open/close/toggle) + window ⌘K / Ctrl+K
// keydown listener + modal suppression guard + route-observer for recents.
//
// Mounts CommandPalette via a React portal so it is immune to any CSS
// transform/containing-block on the layout wrapper; palette unmounts on close
// so per-open state resets for free.
//
// Keybinding predicate: k + (Meta|Ctrl), NOT Shift, NOT Alt.
// Modal suppression: ⌘K is silenced when another [role=dialog][aria-modal=true]
// is open AND that dialog does not carry data-palette (i.e. is not the palette
// itself — toggling the palette while it is open should still close it).
//
// Route-observer: on every navigation to /manage/contracts/:id (excluding /new)
// the provider records a visit so "recently visited contracts" shows up as soon
// as the palette opens, without any explicit user action in the palette itself.
// Title is read from the ["managed-contracts", id] React Query cache entry (the
// contract-detail page fetches it anyway). If the cache is warm the visit is
// recorded synchronously; a cold-cache page load records on the next re-render
// once the query settles.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useMatch, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CommandPalette } from "./CommandPalette";
import { useNavRecents } from "../../hooks/useNavRecents";

// ─── Context ──────────────────────────────────────────────────────────────────

interface PaletteContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const PaletteContext = createContext<PaletteContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function usePaletteContext(): PaletteContextValue {
  const ctx = useContext(PaletteContext);
  if (!ctx) throw new Error("usePaletteContext must be used within PaletteProvider");
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface PaletteProviderProps {
  children: ReactNode;
}

export function PaletteProvider({ children }: PaletteProviderProps) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Route-observer deps
  const queryClient = useQueryClient();
  const location = useLocation();
  const contractMatch = useMatch("/manage/contracts/:id");
  const { recordVisit } = useNavRecents();

  // Track the last-recorded id so we don't fire on every minor
  // search-param change on the same contract page.
  const lastRecordedIdRef = useRef<string | null>(null);

  // Record a visit whenever the user navigates to a contract detail page.
  // Title is sourced from the React Query cache; if the cache is cold the
  // entry will be undefined and we silently skip (the contract-page query
  // will settle on the next render and recordVisit will fire then).
  useEffect(() => {
    const id = contractMatch?.params.id;
    if (!id || id === "new") return;
    if (lastRecordedIdRef.current === id) return; // already recorded this contract

    // Try to read the title from the cache. The contract detail page fetches
    // ["managed-contracts", id] so it should be warm once the page loads.
    const cached = queryClient.getQueryData<{ title?: string; contractNumber?: string }>(
      ["managed-contracts", id],
    );
    const title = cached?.title ?? "";
    if (!title) return; // cache cold — will fire on next render after query settles

    lastRecordedIdRef.current = id;
    recordVisit({
      id,
      title,
      ctx: cached?.contractNumber,
      pathname: location.pathname,
      search: location.search,
    });
  }, [contractMatch, location, queryClient, recordVisit]);

  // Reset the last-recorded ref when navigating away from a contract route
  // so returning to the same contract (e.g. after visiting another route)
  // records the visit again with the current tab/search state.
  useEffect(() => {
    if (!contractMatch) {
      lastRecordedIdRef.current = null;
    }
  }, [contractMatch]);

  // Window-level ⌘K / Ctrl+K handler.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (
        e.key.toLowerCase() !== "k" ||
        !(e.metaKey || e.ctrlKey) ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }

      // Suppress when another modal dialog (not the palette) is already open.
      const otherModal = document.querySelector(
        '[role="dialog"][aria-modal="true"]:not([data-palette])',
      );
      if (otherModal) return;

      e.preventDefault();
      setIsOpen((prev) => !prev);
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <PaletteContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
      {createPortal(
        <CommandPalette isOpen={isOpen} onClose={close} />,
        document.body,
      )}
    </PaletteContext.Provider>
  );
}
